import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "../components/ui/scroll-area";
import AlertCard from "./alerts/AlertCard";

export default function AlertFeed({ alerts = [], onResolve, onAssign, onDelete, onVote, onFocus, focusAlertId, user }) {
  const { t } = useTranslation();
  // Whenever the focused alert changes (from EITHER source — feed click or
  // map marker click), scroll the matching card into view AND apply a
  // brief blue ring highlight. The ring fades after 1.6 s so the card
  // returns to its normal styling.
  const containerRef = useRef(null);
  const [pulse, setPulse] = useState(null);
  useEffect(() => {
    if (!focusAlertId) return;
    setPulse(focusAlertId);
    // Scroll into view inside the feed container (not the whole page) so we
    // don't disturb the user's overall scroll position on desktop.
    const node = containerRef.current?.querySelector(`[data-testid="alert-item-${focusAlertId}"]`);
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    const t = setTimeout(() => setPulse(null), 1600);
    return () => clearTimeout(t);
  }, [focusAlertId]);

  const isAdmin = user?.role === "system_admin" || user?.role === "govt_admin";

  return (
    <div className="h-full flex flex-col" data-testid="alert-feed">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500">
          {t("alert_feed.title", { count: alerts.length })}
        </p>
        {isAdmin && alerts.length > 0 && onDelete && (
          <button
            onClick={() => {
              if (window.confirm(t("alert_feed.clear_all_confirm", { n: alerts.length }))) {
                onDelete("__CLEAR_ALL__");
              }
            }}
            className="text-[10px] text-gray-400 hover:text-red-600 font-body"
            data-testid="clear-all-alerts"
          >
            {t("alert_feed.clear_all")}
          </button>
        )}
      </div>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2" ref={containerRef}>
          {alerts.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8 font-body">
              {t("alert_feed.empty")}
            </p>
          )}
          {alerts.map((alert) => (
            <AlertCard
              key={alert.alert_id}
              alert={alert}
              user={user}
              isAdmin={isAdmin}
              pulse={pulse === alert.alert_id}
              onResolve={onResolve}
              onAssign={onAssign}
              onDelete={onDelete}
              onVote={onVote}
              onFocus={onFocus}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
